/* eslint-disable no-console */
// @flow
import React, {
    useCallback,
    useState,
    useEffect,
    useRef,
    type Node
} from 'react';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import sendbirdSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import GroupChannelHandler from '@sendbird/uikit-react/handlers/GroupChannelHandler';
import { useDispatch } from 'react-redux';
import ChatDrawer from 'common/modules/chat/components/ChatDrawer';
import TabVisibility from 'common/components/core/tab_visibility';
import useChatGlobalState from 'common/modules/chat/hooks/useChatGlobalState';
import { ChatActions } from 'common/modules/chat/redux/actions/chat';

type Props = {
    userId: string,
    userToken: string
};

const ChatApp = ({ userId, userToken }: Props): Node => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(1);
    const [isMessageWindowOpen, setIsMessageWindowOpen] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const connectionToggleTimeoutId = useRef();

    const context = useSendbirdStateContext();
    const dispatch = useDispatch();

    const { isConnected } = useChatGlobalState();

    const sdkInstance = sendbirdSelectors.getSdk(context);
    const { initialized } = context.stores.sdkStore;

    const isConversationTabSelected = selectedTabIndex === 1;

    const openMessageWindow = () => setIsMessageWindowOpen(true);
    const closeMessageWindow = () => setIsMessageWindowOpen(false);

    const closeChatWindow = () => {
        dispatch(ChatActions.hideChatWindow());
    };

    const connectionToggleHandler = useCallback(
        (action: string, cb: () => void) => {
            if (action === 'connect') {
                if (connectionToggleTimeoutId.current) {
                    clearTimeout(connectionToggleTimeoutId.current);
                }
                if (isConnected) {
                    return;
                }
                cb();
            } else {
                if (!isConnected) {
                    return;
                }
                connectionToggleTimeoutId.current = setTimeout(cb, 5000);
            }
        },
        [isConnected]
    );

    const triggerTabVisibilty = useCallback(
        ({ isTabVisible }) => {
            const disConnectSendbirdSdk = () => {
                const disConnectToSendBird = sendbirdSelectors.getDisconnect(
                    context
                );
                disConnectToSendBird()
                    .then(() => {
                        // mark disconnected
                        dispatch(ChatActions.markDisconnected());
                    })
                    .catch(e => {
                        console.error(
                            'Chat error: unable to disconnect from sendbird',
                            e
                        );
                    });
            };

            const connectToSendBirdSdk = () => {
                const connectToSendBird = sendbirdSelectors.getConnect(context);
                connectToSendBird(userId, userToken)
                    .then(() => {
                        // connected
                        dispatch(ChatActions.markConnected());
                        if (selectedChannel) {
                            openMessageWindow();
                        } else if (
                            isConversationTabSelected &&
                            navigator.onLine
                        ) {
                            // Toggling the tab so that channel list is reloaded
                            setSelectedTabIndex(0);
                            setSelectedTabIndex(1);
                        }
                    })
                    .catch(e => {
                        console.error(
                            'Chat error: unable to connect to sendbird',
                            e
                        );
                    });
            };

            if (userId && userToken && initialized && navigator.onLine) {
                // if not visible, disconnect the sendbird connection
                if (!isTabVisible) {
                    closeMessageWindow();
                    connectionToggleHandler(
                        'disconnect',
                        disConnectSendbirdSdk
                    );
                } else {
                    // re-establish the sendbird connection when tab is visible
                    connectionToggleHandler('connect', connectToSendBirdSdk);
                }
            }
        },
        [
            context,
            userId,
            userToken,
            initialized,
            selectedChannel,
            isConversationTabSelected,
            dispatch,
            connectionToggleHandler
        ]
    );

    useEffect(() => {
        if (initialized) {
            // Mark connected
            dispatch(ChatActions.markConnected());
        }
    }, [initialized, dispatch]);

    // fetch unread conversations
    const { groupChannel } = sdkInstance;
    useEffect(() => {
        const fetchAndUpdateUnreadChannelCount = () => {
            groupChannel
                .getTotalUnreadChannelCount()
                .then(res => {
                    // set unread channel count
                    dispatch(ChatActions.setUnreadChannelCount(res));
                })
                .catch(e => {
                    console.error('Chat error: Unread count fetch error', e);
                });
        };
        if (groupChannel && isConnected) {
            fetchAndUpdateUnreadChannelCount();

            const groupChannelEventHandler = new GroupChannelHandler({
                onMessageReceived: () => {
                    fetchAndUpdateUnreadChannelCount();
                }
            });
            groupChannel.addGroupChannelHandler(
                'unreadChannelCount',
                groupChannelEventHandler
            );
            return () => {
                groupChannel.removeGroupChannelHandler('unreadChannelCount');
            };
        }
    }, [groupChannel, isConnected, dispatch]);

    return (
        <>
            <TabVisibility onChange={triggerTabVisibilty} />
            <ChatDrawer
                selectedChannel={selectedChannel}
                selectedTabIndex={selectedTabIndex}
                isMessageWindowOpen={isMessageWindowOpen}
                onTabSelect={setSelectedTabIndex}
                onChannelSelect={setSelectedChannel}
                handleMessageWindowOpen={openMessageWindow}
                handleMessageWindowClose={closeMessageWindow}
                onCloseChatWindow={closeChatWindow}
            />
        </>
    );
};

export default ChatApp;
